// src/components/common/vibe/VibeFeed.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Filter, TrendingUp, Users, Building, User, Bookmark } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../hooks/useAuth';
import vibeService from '../../../services/vibe.service';
import { FILTER_OPTIONS, SORT_OPTIONS } from '../../../constants/vibeConstants';

// Import components
import CreatePost from './CreatePost';
import PostCard from './PostCard';
import RecognitionHub from './RecognitionHub';
import VoiceToHR from './VoiceToHR';
import VoiceToHRDashboard from './VoiceToHRDashboard';
import CelebrationHub from './CelebrationHub';
import AnnouncementBox from './AnnouncementBox';

const VibeFeed = ({
    filter: initialFilter = 'company',
    profileUserId = null,
    showHeader = true,
    showCreatePost = true,
    showSidebar = true,
    maxPosts = null,
    isProfileView = false
}) => {
    const { theme } = useTheme();
    const { user, isAdmin } = useAuth();
    const navigate = useNavigate();

    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [activeFilter, setActiveFilter] = useState(initialFilter);
    const [sortBy, setSortBy] = useState('recent');
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);

    useEffect(() => {
        // //console.log('👤 VibeFeed - User info:', {
        //     role: user?.role,
        //     isAdmin,
        //     shouldShowDashboard: isAdmin || ['Super Admin', 'HR Admin'].includes(user?.role)
        // });
        loadPosts(true);
    }, [activeFilter, sortBy]);

    const loadPosts = async (reset = false) => {
        const currentOffset = reset ? 0 : offset;

        //console.log(`🔄 Loading vibe feed (reset: ${reset}, filter: ${activeFilter}, sortBy: ${sortBy})`);

        if (reset && posts.length === 0) {
            // Initial load - show loading screen
            setLoading(true);
        } else {
            // Refresh or load more - show refreshing indicator
            setRefreshing(true);
        }

        try {
            const limit = maxPosts || 10;
            const feedParams = {
                sortBy: sortBy,
                limit: maxPosts || (maxPosts && limit * 2) || 10,
                offset: currentOffset
            };

            // Handle different filtering scenarios
            if (activeFilter === 'user_vibes' && profileUserId) {
                // When viewing someone else's profile, get their posts
                feedParams.user_id = profileUserId;
                // Also set filter to company to get broader feed, then we'll filter client-side
                feedParams.filter = 'company';
            } else {
                // Use the standard filter for other cases
                feedParams.filter = activeFilter;
            }

            console.log('🔄 Loading vibe feed with params:', {
                ...feedParams,
                activeFilter,
                profileUserId,
                isProfileView,
                currentUser: user?.id,
                currentUserRole: user?.role
            });

            const response = await vibeService.getFeed(feedParams);

            const fetchedPosts = response.data || [];

            // Apply client-side filtering for user_vibes
            let filteredPosts = fetchedPosts;
            if (activeFilter === 'user_vibes' && profileUserId) {
                filteredPosts = fetchedPosts.filter(post => post.employee_id === profileUserId);
                console.log(`🎯 Client-side filtering: ${fetchedPosts.length} → ${filteredPosts.length} posts for user ${profileUserId}`);
            }

            // Apply maxPosts limit if specified
            const limitedPosts = maxPosts ? filteredPosts.slice(0, maxPosts) : filteredPosts;

            if (reset) {
                setPosts(limitedPosts);
                setOffset(limit);
                //console.log('📝 Posts replaced (reset mode)');
            } else {
                setPosts(prev => {
                    const combined = [...prev, ...limitedPosts];
                    return maxPosts ? combined.slice(0, maxPosts) : combined;
                });
                setOffset(prev => prev + limit);
                //console.log('📝 Posts appended (load more mode)');
            }

            setHasMore(!maxPosts && (response.pagination?.hasMore || false));
        } catch (error) {
            console.error('❌ Error loading vibe feed:', error);
            console.error('Error details:', {
                message: error.message,
                response: error.response?.data
            });
        } finally {
            setLoading(false);
            setRefreshing(false);
            //console.log('🏁 Load posts completed');
        }
    };

    const handlePostCreated = async (postData) => {
        //console.log('📢 handlePostCreated called', { hasPostData: !!postData });
        // If postData is provided, it's a regular post
        // If not provided, it's a poll (already created by CreatePoll component)
        if (postData) {
            //console.log('✍️ Creating regular post...');
            await vibeService.createPost(postData);
            //console.log('✅ Regular post created');
        } else {
            //console.log('📊 Poll already created, just refreshing feed...');
        }
        // Refresh feed in both cases
        //console.log('🔄 Calling loadPosts(true) to refresh feed...');
        await loadPosts(true);
        //console.log('✅ handlePostCreated completed');
    };

    const handleReaction = async (postId, reactionType) => {
        await vibeService.toggleReaction(postId, reactionType);
        await loadPosts(true);
    };

    const handleComment = async () => {
        await loadPosts(true);
    };

    const handleDeletePost = async (postId) => {
        if (!window.confirm('Delete this post?')) return;
        await vibeService.deletePost(postId);
        await loadPosts(true);
    };

    const handleSavePost = async (postId) => {
        await vibeService.toggleSavePost(postId);
        await loadPosts(true);
    };

    const getFilterIcon = (filterValue) => {
        switch (filterValue) {
            case 'company': return Building;
            case 'my_vibes': return User;
            case 'saved_vibes': return Bookmark;
            case 'following': return Users;
            default: return Building;
        }
    };

    if (loading && posts.length === 0) {
        return (
            <div className={`min-h-screen flex items-center justify-center ${
                theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'
            }`}>
                <div className="text-center">
                    <RefreshCw className={`w-12 h-12 animate-spin mx-auto mb-4 ${
                        theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                        Loading Vibe Feed...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={`${!showHeader ? '' : 'min-h-screen'} ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'}`}>
            <div className={`${!showHeader ? '' : 'max-w-7xl mx-auto p-6'}`}>
                {/* Header */}
                {showHeader && (
                    <div className="tour-vibe-header mb-6">
                        <h1 className={`text-3xl font-bold ${
                            theme === 'dark' ? 'text-white' : 'text-gray-900'
                        }`}>
                            Vibe 🌟
                        </h1>
                        <p className={`text-sm mt-1 ${
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}>
                            Share your thoughts, celebrate wins, and connect with your team
                        </p>
                    </div>
                )}

                {/* Filters and Refresh */}
                {showHeader && (
                    <div className="tour-vibe-filters flex items-center gap-3 mb-6 overflow-x-auto pb-2">
                        {FILTER_OPTIONS.map((filter) => {
                            const Icon = getFilterIcon(filter.value);
                            return (
                                <button
                                    key={filter.value}
                                    onClick={() => setActiveFilter(filter.value)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                                        activeFilter === filter.value
                                            ? 'bg-blue-600 text-white'
                                            : theme === 'dark'
                                                ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                                : 'bg-white text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    <Icon className="w-4 h-4" />
                                    {filter.label}
                                </button>
                            );
                        })}

                        <div className="flex-1" />

                        <button
                            onClick={() => loadPosts(true)}
                            disabled={refreshing}
                            className={`p-2 rounded-lg border ${
                                theme === 'dark'
                                    ? 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                                    : 'bg-white border-gray-300 hover:bg-gray-50'
                            } ${refreshing ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                )}

                {/* Main Grid */}
                <div className={`grid ${showSidebar ? 'grid-cols-1 lg:grid-cols-3 gap-6' : 'grid-cols-1'}`}>
                    {/* Left Column - Feed */}
                    <div className={`tour-vibe-feed-column ${showSidebar ? 'lg:col-span-2' : ''} space-y-6`}>
                        {/* Create Post */}
                        {showCreatePost && (
                            <div className="tour-vibe-create-post">
                                <CreatePost
                                    onPostCreated={handlePostCreated}
                                    currentUser={user}
                                    theme={theme}
                                />
                            </div>
                        )}

                        {/* Posts */}
                        {posts.length === 0 ? (
                            <div className={`rounded-xl p-12 border text-center ${
                                theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                            }`}>
                                <TrendingUp className={`w-16 h-16 mx-auto mb-4 ${
                                    theme === 'dark' ? 'text-gray-600' : 'text-gray-400'
                                }`} />
                                <h3 className={`text-xl font-semibold mb-2 ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                    No posts yet
                                </h3>
                                <p className={`text-sm ${
                                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                }`}>
                                    Be the first to share your vibe!
                                </p>
                            </div>
                        ) : (
                            <>
                                {posts.map((post) => (
                                    <PostCard
                                        key={post.id}
                                        post={post}
                                        currentUser={user}
                                        onReaction={handleReaction}
                                        onComment={handleComment}
                                        onDelete={handleDeletePost}
                                        onSave={handleSavePost}
                                        onEdit={handleComment}
                                        theme={theme}
                                    />
                                ))}

                                {/* Load More */}
                                {hasMore && !maxPosts && (
                                    <button
                                        onClick={() => loadPosts(false)}
                                        disabled={refreshing}
                                        className={`w-full py-3 rounded-lg font-medium transition-colors ${
                                            refreshing
                                                ? theme === 'dark'
                                                    ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                : theme === 'dark'
                                                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                                    : 'bg-white text-gray-700 hover:bg-gray-100'
                                        }`}
                                    >
                                        {refreshing ? 'Loading...' : 'Load More'}
                                    </button>
                                )}
                            </>
                        )}
                    </div>

                    {/* Right Column - Announcement Box, Voice to HR / Dashboard, Recognition Hub & Stats */}
                    {showSidebar && (
                        <div className="tour-vibe-sidebar space-y-6">
                            {/* Announcement Box Widget - Company-wide announcements */}
                            <div className="tour-vibe-sidebar-announcements">
                                <AnnouncementBox />
                            </div>

                            {/* Voice to HR Widget or Dashboard based on role */}
                            <div className="tour-vibe-sidebar-voice">
                                {isAdmin || ['Super Admin', 'HR Admin'].includes(user?.role) ? (
                                    <VoiceToHRDashboard theme={theme} />
                                ) : (
                                    <VoiceToHR theme={theme} />
                                )}
                            </div>

                            {/* Recognition Hub */}
                            <div className="tour-vibe-sidebar-recognition">
                                <RecognitionHub theme={theme} onKudosGiven={() => loadPosts(true)} />
                            </div>

                            {/* Celebration Hub - Birthdays and Work Anniversaries */}
                            <div className="tour-vibe-sidebar-celebration">
                                <CelebrationHub theme={theme} onPostCreated={handlePostCreated} />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VibeFeed;
